/* eslint-disable ngrx/prefer-effect-callback-in-block-statement */
/* eslint-disable @typescript-eslint/no-explicit-any */
import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Dictionary } from '@ngrx/entity';
import { concatLatestFrom } from '@ngrx/operators';
import { Action, Store } from '@ngrx/store';
import { EMPTY, Observable } from 'rxjs';
import { concatMap, map, switchMap } from 'rxjs/operators';

import { AutoPayUtilService } from '@amfam/billing/auto-pay/feature';
import { BillAccountsSelectors } from '@amfam/billing/billaccount/data-access';
import { PaymentMethodSelectors } from '@amfam/billing/payment-method/data-access';
import { PaymentMethod } from '@amfam/billing/payment-method/util';
import { PaymentAccountActions } from '@amfam/billing/paymentaccount/data-access';
import { pendingPaymentQuery } from '@amfam/billing/pending-payment/data-access';
import {
  ScheduledPaymentActions,
  ScheduledPaymentSelectors
} from '@amfam/billing/schedulepayment/data-access';
import {
  BillingPaymentPaths,
  ScheduledPayment,
  SubmitScheduledPaymentPayloadModel
} from '@amfam/shared/models';
import { FeatureFlagService } from '@amfam/shared/utility/feature-flag/data-access';
import { fromRouterActions } from '@amfam/shared/utility/navigation';
import {
  Applications,
  ApplicationService,
  CopyService
} from '@amfam/shared/utility/shared-services';

import { OneTimePayUtilService } from '../..';
import * as OneTimePayActions from './one-time-pay-feature.actions';
import { OneTimePayFeatureFacade } from './one-time-pay-feature.facade';
import { PaymentMethodRelatedPaymentErrorCodes } from './one-time-pay-feature.models';
import * as OneTimePaySelectors from './one-time-pay-feature.selectors';

@Injectable()
export class OneTimePayFeatureEffects {
  loadOneTimePayFeatures$ = createEffect(() =>
    this.actions$.pipe(
      ofType(OneTimePayActions.loadOneTimePays),
      /** An EMPTY observable only emits completion. Replace with your own observable API request */
      concatMap(() => EMPTY as Observable<{ type: string }>)
    )
  );

  routeToEntryPoint$ = createEffect(() =>
    this.actions$.pipe(
      ofType(OneTimePayActions.goToCancelPath),
      concatLatestFrom(() => this.store.select(OneTimePaySelectors.selectOneTimePayCancelPath)),
      map(([, cancelPath]) =>
        fromRouterActions.Go({
          path: [
            cancelPath.includes('confirmation')
              ? '/billing'
              : cancelPath.includes('review-payment')
                ? '/billing'
                : cancelPath
          ]
        })
      )
    )
  );

  submitOTPAdd$ = createEffect(() =>
    this.actions$.pipe(
      ofType(OneTimePayActions.submitOTP),
      concatLatestFrom(() => {
        return [
          this.store.select(OneTimePaySelectors.selectOneTimePayEntities),
          this.store.select(PaymentMethodSelectors.selectPaymentMethodEntities),
          this.store.select(BillAccountsSelectors.selectBillAccountEntities),
          this.store.select(ScheduledPaymentSelectors.selectScheduledPaymentEntities)
        ] satisfies [
          Observable<unknown>,
          Observable<unknown>,
          Observable<unknown>,
          Observable<unknown>
        ];
      }),
      switchMap(
        ([, otpEntities, paymentMethodEntities, billAccountEntities, scheduledPaymentEntities]) => {
          const paymentAccountEntities: Dictionary<PaymentMethod> = {};
          for (const key in paymentMethodEntities) {
            if (paymentMethodEntities[key]) {
              paymentAccountEntities[paymentMethodEntities[key].nickName] =
                paymentMethodEntities[key];
            }
          }
          scheduledPaymentEntities = scheduledPaymentEntities as Dictionary<ScheduledPayment>;
          const otpTransformedPayload: SubmitScheduledPaymentPayloadModel =
            this.otpService.transformOTPentities(
              otpEntities,
              paymentAccountEntities,
              billAccountEntities,
              scheduledPaymentEntities
            );
          const selectedPaymentAccount =
            paymentAccountEntities[
              otpTransformedPayload?.requestJson?.payment?.paymentAccount?.nickName
            ];

          const isAdminAppWithoutModeOfAuthORAL =
            this.applicationService.isApp(Applications.MYACCOUNT_ADMIN) &&
            selectedPaymentAccount.modeOfAuthorization !== 'ORAL';

          const isNotAMyaccountAdminAppWithoutESIGN =
            !this.applicationService.isApp(Applications.MYACCOUNT_ADMIN) &&
            selectedPaymentAccount.modeOfAuthorization !== 'ESIGNATURE' &&
            !!selectedPaymentAccount.achWithdrawal;

          if (isNotAMyaccountAdminAppWithoutESIGN || isAdminAppWithoutModeOfAuthORAL) {
            return [
              PaymentAccountActions.PaymentAccountsUpdateModeAuth(
                this.otpService.buildOTPPayloadforUpdateModeOfAuth({
                  paymentAccount: selectedPaymentAccount,
                  requestPayload: otpTransformedPayload,
                  isAdminAppWithoutModeOfAuthORAL,
                  isNotAMyaccountAdminAppWithoutESIGN,
                  action: 'add'
                })
              )
            ];
          }
          return [
            ScheduledPaymentActions.scheduledPaymentsSubmit(
              this.otpService.transformOTPentities(
                otpEntities,
                paymentAccountEntities,
                billAccountEntities,
                scheduledPaymentEntities
              )
            )
          ];
        }
      )
    )
  );

  submitOTPEdit$ = createEffect(() =>
    this.actions$.pipe(
      ofType(OneTimePayActions.editOTP),
      concatLatestFrom(
        () =>
          [
            this.store.select(OneTimePaySelectors.selectOneTimePayEntities),
            this.store.select(PaymentMethodSelectors.selectPaymentMethodEntities),
            this.store.select(BillAccountsSelectors.selectBillAccountEntities),
            this.store.select(ScheduledPaymentSelectors.selectScheduledPaymentEntities)
          ] satisfies [
            Observable<unknown>,
            Observable<unknown>,
            Observable<unknown>,
            Observable<unknown>
          ]
      ),
      switchMap(
        ([, OTPentities, paymentMethodEntities, billAccountEntities, scheduledPaymentEntities]) => {
          const paymentAccountEntities: Dictionary<PaymentMethod> = {};
          for (const key in paymentMethodEntities) {
            if (paymentMethodEntities[key]) {
              paymentAccountEntities[paymentMethodEntities[key].nickName] =
                paymentMethodEntities[key];
            }
          }
          scheduledPaymentEntities = scheduledPaymentEntities as Dictionary<ScheduledPayment>;
          const otpTransformedPayload: SubmitScheduledPaymentPayloadModel =
            this.otpService.transformOTPentities(
              OTPentities,
              paymentAccountEntities,
              billAccountEntities,
              scheduledPaymentEntities
            );
          const selectedPaymentAccount =
            paymentAccountEntities[
              otpTransformedPayload?.requestJson?.payment?.paymentAccount?.nickName
            ];

          const isAdminAppWithoutModeOfAuthORAL =
            this.applicationService.isApp(Applications.MYACCOUNT_ADMIN) &&
            selectedPaymentAccount.modeOfAuthorization !== 'ORAL';

          const isNotAMyaccountAdminAppWithoutESIGN =
            !this.applicationService.isApp(Applications.MYACCOUNT_ADMIN) &&
            selectedPaymentAccount.modeOfAuthorization !== 'ESIGNATURE' &&
            !!selectedPaymentAccount.achWithdrawal;

          if (isNotAMyaccountAdminAppWithoutESIGN || isAdminAppWithoutModeOfAuthORAL) {
            return [
              PaymentAccountActions.PaymentAccountsUpdateModeAuth(
                this.otpService.buildOTPPayloadforUpdateModeOfAuth({
                  paymentAccount: selectedPaymentAccount,
                  requestPayload: otpTransformedPayload,
                  isAdminAppWithoutModeOfAuthORAL,
                  isNotAMyaccountAdminAppWithoutESIGN,
                  action: 'edit'
                })
              )
            ];
          }
          return [
            ScheduledPaymentActions.scheduledPaymentsEdit(
              this.otpService.transformOTPentities(
                OTPentities,
                paymentAccountEntities,
                billAccountEntities,
                scheduledPaymentEntities
              ).requestJson
            )
          ];
        }
      )
    )
  );

  routeToConfirmation$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        ScheduledPaymentActions.ScheduledPaymentsActionTypes.ScheduledPaymentsSubmitSuccessType,
        ScheduledPaymentActions.ScheduledPaymentsActionTypes.ScheduledPaymentsEditSuccessType,
        ScheduledPaymentActions.ScheduledPaymentsActionTypes.ScheduledPaymentsDeleteSuccessType,
        ScheduledPaymentActions.ScheduledPaymentsActionTypes.ScheduledPaymentsSubmitFailType,
        ScheduledPaymentActions.ScheduledPaymentsActionTypes.ScheduledPaymentsEditFailType,
        ScheduledPaymentActions.ScheduledPaymentsActionTypes.ScheduledPaymentsDeleteFailType
      ),
      switchMap((action: any) => {
        const returnArr: Action[] = [];
        if (
          action.type ===
            ScheduledPaymentActions.ScheduledPaymentsActionTypes
              .ScheduledPaymentsSubmitSuccessType ||
          action.type ===
            ScheduledPaymentActions.ScheduledPaymentsActionTypes.ScheduledPaymentsEditSuccessType
        ) {
          returnArr.push(OneTimePayActions.setOneTimePayOperation({ operation: 'Success' }));
        }
        if (
          action.type ===
            ScheduledPaymentActions.ScheduledPaymentsActionTypes.ScheduledPaymentsSubmitFailType ||
          action.type ===
            ScheduledPaymentActions.ScheduledPaymentsActionTypes.ScheduledPaymentsEditFailType
        ) {
          if (this.featureFlagService.isEnabled('payment_failure_vague_error')) {
            const errorActions = [];
            // reset payment error code in case of Try Again function
            errorActions.push(OneTimePayActions.storePaymentErrorCode({ paymentErrorCode: null }));
            // store payment error code to execute different results
            const paymentErrorCode: string = action.payload.status?.status?.messages
              ? action.payload.status.status.messages[0].code
              : action.payload.status?.messages
                ? action.payload.status.messages[0].code
                : null;
            errorActions.push(OneTimePayActions.storePaymentErrorCode({ paymentErrorCode }));
            const isPaymentMethodRelatedPaymentErrorCode =
              PaymentMethodRelatedPaymentErrorCodes.includes(paymentErrorCode);
            if (!isPaymentMethodRelatedPaymentErrorCode) {
              // reset OneTimePayOperation for Try Again function
              errorActions.push(OneTimePayActions.setOneTimePayOperation({ operation: '' }));
              errorActions.push(OneTimePayActions.setOneTimePayOperation({ operation: 'Error' }));
              errorActions.push(
                fromRouterActions.Go({
                  path: [BillingPaymentPaths.CONFIRMATION_ONE_TIME_PAYMENT]
                })
              );
            }
            return errorActions;
          } else {
            // reset OneTimePayOperation for Try Again function
            returnArr.push(OneTimePayActions.setOneTimePayOperation({ operation: '' }));
            returnArr.push(OneTimePayActions.setOneTimePayOperation({ operation: 'Error' }));
          }
        }
        if (
          action.type ===
          ScheduledPaymentActions.ScheduledPaymentsActionTypes.ScheduledPaymentsDeleteSuccessType
        ) {
          returnArr.push(OneTimePayActions.setOneTimePayOperation({ operation: 'Delete-Success' }));
        }
        if (
          action.type ===
          ScheduledPaymentActions.ScheduledPaymentsActionTypes.ScheduledPaymentsDeleteFailType
        ) {
          // reset OneTimePayOperation for Try Again function
          returnArr.push(OneTimePayActions.setOneTimePayOperation({ operation: '' }));
          returnArr.push(OneTimePayActions.setOneTimePayOperation({ operation: 'Delete-Error' }));
        }
        returnArr.push(
          fromRouterActions.Go({
            path: [
              this.featureFlagService.isEnabled('simplified_payment_p2') &&
              action.type ===
                ScheduledPaymentActions.ScheduledPaymentsActionTypes
                  .ScheduledPaymentsSubmitSuccessType
                ? BillingPaymentPaths.SIMPLIFIED_PAYMENT_CONFIRMATION
                : BillingPaymentPaths.CONFIRMATION_ONE_TIME_PAYMENT
            ]
          })
        );
        return returnArr;
      })
    )
  );

  routeToMakeAnotherPayment$ = createEffect(() =>
    this.actions$.pipe(
      ofType(OneTimePayActions.goToMakeAnotherPayment),
      concatLatestFrom(() =>
        this.store.select(pendingPaymentQuery.selectBillAccountsEligibleForOneTimePay)
      ),
      map(([, eligibleAccounts]) => {
        if (eligibleAccounts.length === 1) {
          return fromRouterActions.Go({ path: [BillingPaymentPaths.REVIEW_ONE_TIME_PAYMENT] });
        } else if (eligibleAccounts.length > 1) {
          return fromRouterActions.Go({ path: [BillingPaymentPaths.ADD_ONE_TIME_PAYMENT] });
        } else {
          return fromRouterActions.Go({ path: [BillingPaymentPaths.BILLING_SECTION_PAGE] });
        }
      })
    )
  );

  routeToBilling$ = createEffect(() =>
    this.actions$.pipe(
      ofType(OneTimePayActions.goToBilling),
      map(() => fromRouterActions.Go({ path: [BillingPaymentPaths.BILLING_SECTION_PAGE] }))
    )
  );

  routeToReviewForEdit$ = createEffect(() =>
    this.actions$.pipe(
      ofType(OneTimePayActions.routeToReviewForEdit),
      map(action => action.paymentId),
      concatLatestFrom(() => this.otpFeatureFacade.getScheduledPaymentEntities()),
      map(([paymentId, scheduledPaymentEntities]) => {
        this.otpFeatureFacade.setupEditOTP(
          paymentId,
          scheduledPaymentEntities as Dictionary<ScheduledPayment>
        );
        return fromRouterActions.Go({ path: [BillingPaymentPaths.REVIEW_ONE_TIME_PAYMENT] });
      })
    )
  );

  /**
   * @author: Abhishek Singh
   * @returns:
   * @description: This effect triggers a analytics notification.
   */

  analyticsNotification$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(OneTimePayActions.sendOtpAnalytics),
        map(action => action.payload),
        map(payload => this.otpService.sendOTPAnalytics(payload))
      ),
    { dispatch: false }
  );

  leaveOTPFlow$ = createEffect(() =>
    this.actions$.pipe(
      ofType(OneTimePayActions.leaveOTPFlow),
      map(action => {
        switch (action.id) {
          case this.copyService.getCopy(
            `billing.oneTimePayRefactor.alertMessages.body2Text-manageOTP`
          ):
            return fromRouterActions.Go({
              path: [BillingPaymentPaths.BILLING_DETAIL_PAGE, action.billAccountNumber],
              query: { openAccordion: 'manageOTP' }
            });

          case this.copyService.getCopy(
            `billing.oneTimePayRefactor.alertMessages.body2Text-hasAutoPay`
          ):
            this.autopayUtil.editAutoPay(action.billAccountNumber, '/billing');
            break;
          case this.copyService.getCopy(
            `billing.oneTimePayRefactor.alertMessages.setupAutopayText`
          ):
            this.autopayUtil.routeToMultipleAutoPaySelection('/billing');
            break;
          default:
            return OneTimePayActions.goToCancelPath();
        }
      })
    )
  );

  constructor(
    private actions$: Actions,
    private store: Store,
    private otpService: OneTimePayUtilService,
    private applicationService: ApplicationService,
    private featureFlagService: FeatureFlagService,
    private otpFeatureFacade: OneTimePayFeatureFacade,
    private autopayUtil: AutoPayUtilService,
    private copyService: CopyService
  ) {}
}
